import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { isClickedOutside, lockScroll, unlockScroll, onAnimationEnd } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

/**
 * diviATE! Cart Drawer Shell
 *
 * Fully custom replacement for Horizon's <theme-drawer><dialog> cart shell.
 * Horizon's native drawer picks between a true modal (`dialog.showModal()`,
 * native top layer, real ::backdrop) and a non-modal "squeeze" mode
 * (`dialog.show()`, no backdrop, page content pushed aside) based on a
 * viewport media query. On some mobile browsers that squeeze mode ended up
 * triggering, or the dialog's z-index (relative to a --layer-sticky token)
 * lost a stacking fight against the site header, letting the header render
 * through the drawer. Using plain fixed-position elements instead of
 * <dialog> removes that ambiguity entirely: there is exactly one mode.
 *
 * Everything nested inside (cart-drawer-component, cart-products,
 * quick-add, cart-discount-component, the real checkout form) is untouched
 * Horizon markup — only this outer shell is custom. Section Rendering API
 * still targets the unchanged `#shopify-section-cart-drawer-section`
 * wrapper, so cart mutations still refresh this section's content exactly
 * as before.
 *
 * Still extends Component (required for declarative on:click="#cart-drawer/x"
 * actions to resolve — see assets/component.js) and still dispatches the
 * same "theme-drawer:open"/"theme-drawer:close" event *names* Horizon's own
 * ThemeDrawer uses, so the nested <cart-drawer-component>'s existing
 * DrawerOpenEvent listener (sticky footer height, installments CTA) keeps
 * working unmodified — event listeners match by type string, not by which
 * class constructed the event.
 *
 * Also owns the "auto-open once the cart first reaches a value threshold"
 * rule (replacing Horizon's native "auto-open on every add" behavior,
 * which naturally goes inert here since <cart-drawer-component> looks for
 * an ancestor literally named `theme-drawer` — this element isn't one).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} panel
 * @property {HTMLElement} backdrop
 * @extends {Component<Refs>}
 */
export class CartDrawerDiviate extends Component {
  requiredRefs = ['panel', 'backdrop'];

  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  get isOpen() {
    return this.hasAttribute('open');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    if (this.refs.panel) this.refs.panel.inert = !this.isOpen;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    unlockScroll(this);
    removeTrapFocus();
  }

  open = () => {
    if (this.isOpen) return;

    this.#previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    this.setAttribute('open', '');
    this.refs.panel.inert = false;
    lockScroll(this);
    trapFocus(this.refs.panel);

    document.addEventListener('keydown', this.#onKeyDown);
    this.refs.backdrop.addEventListener('click', this.#onBackdropClick);

    this.dispatchEvent(new CustomEvent('theme-drawer:open', { bubbles: true }));
  };

  close = async () => {
    if (!this.isOpen) return;

    document.removeEventListener('keydown', this.#onKeyDown);
    this.refs.backdrop.removeEventListener('click', this.#onBackdropClick);
    removeTrapFocus();

    this.classList.add('is-closing');
    await onAnimationEnd(this.refs.panel, undefined, { subtree: false });
    this.classList.remove('is-closing');

    this.removeAttribute('open');
    this.refs.panel.inert = true;
    unlockScroll(this);

    this.dispatchEvent(new CustomEvent('theme-drawer:close', { bubbles: true }));

    if (this.#previouslyFocused && document.contains(this.#previouslyFocused)) {
      this.#previouslyFocused.focus();
    }
    this.#previouslyFocused = null;
  };

  toggle = () => {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #onBackdropClick = (event) => {
    if (isClickedOutside(event, this.refs.panel)) {
      this.close();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (event.isComposing || event.defaultPrevented) return;
    event.preventDefault();
    this.close();
  };

  /**
   * Auto-opens the drawer on every genuine add — quantity changes and
   * removals never auto-open.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    if (this.dataset.autoOpen !== 'true') return;
    if (event.action !== 'add') return;
    if (this.isOpen) return;

    event.promise
      ?.then((result) => {
        const { detail } = result;
        if (detail?.didError) return;
        this.open();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer-diviate] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('cart-drawer-diviate')) {
  customElements.define('cart-drawer-diviate', CartDrawerDiviate);
}
